import { useState, useEffect } from 'react';
import { Card, Text, Input, Badge, Spinner } from '@fluentui/react-components';
import { useAuth } from '@/auth/AuthContext';
import './AuditLog.css';

const API_BASE = import.meta.env.VITE_API_BASE || '/api';
const ADMIN_API = `${API_BASE}/portal`;

interface AuditEntry {
  id: string;
  timestamp: string;
  user: string;
  action: string;
  resource: string;
  details: string;
  status: 'success' | 'failed' | 'warning';
}

export default function AuditLogPage() {
  const { token } = useAuth();
  const [logs, setLogs] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterAction, setFilterAction] = useState<string>('all');

  useEffect(() => {
    async function fetchAuditLogs() {
      try {
        setLoading(true);
        const response = await fetch(`${ADMIN_API}/audit-logs`, {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });
        if (!response.ok) {
          throw new Error(`Failed to load audit logs: HTTP ${response.status}`);
        }
        const data = await response.json();
        setLogs(Array.isArray(data?.logs) ? data.logs : []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load audit logs');
        setLogs([]);
      } finally {
        setLoading(false);
      }
    }
    fetchAuditLogs();
  }, [token]);

  const actions = Array.from(new Set(logs.map((l) => l.action)));

  const filteredLogs = logs.filter((log) => {
    const matchesSearch =
      log.user.toLowerCase().includes(searchQuery.toLowerCase()) ||
      log.resource.toLowerCase().includes(searchQuery.toLowerCase()) ||
      log.details.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesAction = filterAction === 'all' || log.action === filterAction;
    return matchesSearch && matchesAction;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success':
        return 'success';
      case 'failed':
        return 'danger';
      case 'warning':
        return 'warning';
      default:
        return 'subtle';
    }
  };

  const getActionCategory = (action: string) => {
    if (action.includes('LOGIN') || action.includes('AUTH')) return '🔐';
    if (action.includes('ADMIN')) return '👤';
    if (action.includes('TENANT') || action.includes('SUSPEND'))
      return '🏢';
    if (action.includes('SETTINGS')) return '⚙️';
    if (action.includes('RATE')) return '📊';
    if (action.includes('EXPORT') || action.includes('VIEW'))
      return '📄';
    return '📋';
  };

  return (
    <div className="auditlog-container">
      <div className="page-header">
        <div>
          <h1>Audit Log</h1>
          <Text size={300}>View all system activities and administrative actions</Text>
        </div>
      </div>

      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
          <Spinner label="Loading audit logs..." />
        </div>
      )}

      {error && (
        <Card style={{ padding: '24px', marginBottom: '16px' }}>
          <Text style={{ color: '#E02424' }}>{error}</Text>
        </Card>
      )}

      {!loading && !error && logs.length === 0 && (
        <Card style={{ padding: '48px', textAlign: 'center' }}>
          <Text size={400} weight="semibold">No audit log entries yet</Text>
          <br />
          <Text size={300}>Audit events will appear here as administrative actions are recorded.</Text>
        </Card>
      )}

      {!loading && logs.length > 0 && (
      <>
      <Card className="filters-card">
        <div className="filters-grid">
          <Input
            placeholder="Search by user, resource, or details..."
            value={searchQuery}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setSearchQuery(e.target.value)
            }
            style={{ flex: 1 }}
          />
          <div className="action-filter">
            <select
              value={filterAction}
              onChange={(e) => setFilterAction(e.target.value)}
              className="action-select"
            >
              <option value="all">All Actions</option>
              {actions.map((action) => (
                <option key={action} value={action}>
                  {action}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      <div className="logs-table-container">
        <table className="logs-table">
          <thead>
            <tr>
              <th>Timestamp</th>
              <th>User</th>
              <th>Action</th>
              <th>Resource</th>
              <th>Details</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {filteredLogs.map((log) => (
              <tr key={log.id}>
                <td>
                  <Text size={200}>
                    {new Date(log.timestamp).toLocaleString()}
                  </Text>
                </td>
                <td>
                  <Text weight="semibold">{log.user}</Text>
                </td>
                <td>
                  <div className="action-cell">
                    <span className="action-icon">
                      {getActionCategory(log.action)}
                    </span>
                    <Text size={200}>{log.action}</Text>
                  </div>
                </td>
                <td>
                  <Text size={200}>{log.resource}</Text>
                </td>
                <td>
                  <Text size={200}>{log.details}</Text>
                </td>
                <td>
                  <Badge
                    appearance="outline"
                    color={getStatusColor(log.status)}
                  >
                    {log.status.charAt(0).toUpperCase() + log.status.slice(1)}
                  </Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Card className="log-stats">
        <h3>Audit Statistics</h3>
        <div className="stats-grid">
          <div className="stat-item">
            <Text size={400} weight="bold" className="stat-value">
              {logs.length}
            </Text>
            <Text size={200}>Total Events</Text>
          </div>
          <div className="stat-item">
            <Text size={400} weight="bold" className="stat-value">
              {logs.filter((l) => l.status === 'success').length}
            </Text>
            <Text size={200}>Successful</Text>
          </div>
          <div className="stat-item">
            <Text size={400} weight="bold" className="stat-value">
              {logs.filter((l) => l.status === 'failed').length}
            </Text>
            <Text size={200}>Failed</Text>
          </div>
          <div className="stat-item">
            <Text size={400} weight="bold" className="stat-value">
              {Array.from(new Set(logs.map((l) => l.user))).length}
            </Text>
            <Text size={200}>Active Users</Text>
          </div>
        </div>
      </Card>
      </>
      )}
    </div>
  );
}
